import { DEMO_PASSWORD } from './seed/customers'
import { localCatalog } from '../data/catalog'
import { newDemoAccount } from './seed/factory'
import { applySubscriptionAction, restoreSnapshot, snapshotOf } from '../core/rules/subscription'
import { newRequestId } from '../core/api/http'
import { photoConsent, policy } from '../data/policies'
import { today } from '../core/format/dates'
import { ApiError } from '../core/api/http'
import { db } from './db'
import { linkGuestKuren } from './kuurOrders'
import { creditBalanceCents } from './voucherBackend'
import { assertBookable, durationOf, sessionFor } from './agendaBackend'
import { passwordProblem } from '../core/rules/password'
import { demoId, reply } from './reply'
import type { Account, Appointment, DiaryEntry } from '../core/types/account'
import type { AccountBackend } from '../core/types/backend'
import type { SubscriptionAction, SubscriptionActionResponse, SubscriptionSnapshot } from '../core/types/requests'

/**
 * DEMO — Mijn omgeving aan de kant van de backend. Sessie in de browser
 * (in het echt een httpOnly-cookie), data in demo/db.ts.
 */

const session = {
  read: (): Account | null => {
    const id = db.sessionId()
    return id ? db.account(id) : null
  },
  start(account: Account) {
    db.putAccount(account)
    db.setSession(account.customer.id)
  },
  /** Wijzigt het ingelogde account en geeft het gewijzigde deel terug. */
  change<T>(fn: (a: Account) => [Account, T]): T {
    const current = session.read()
    if (!current) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    const [next, result] = fn(current)
    db.putAccount(next)
    return result
  },
}

/**
 * "Ongedaan maken" zoals de server het moet doen (docs/LARAVEL.md §10.4): bij elke actie de
 * vorige stand bewaren onder een willekeurig token. Eén keer bruikbaar, 2 minuten geldig, en
 * alleen zolang het abonnement sindsdien niet meer veranderde. In het geheugen: na herladen
 * is terugdraaien voorbij, net als bij een verlopen token.
 */
const UNDO_TTL_MS = 2 * 60 * 1000
type UndoEntry = { customerId: string; before: SubscriptionSnapshot; after: string; expires: number }
const undoEntries = new Map<string, UndoEntry>()
/** Idempotentie per `requestId` (per tabblad; de server bewaart het per klant, 24 uur). */
const subscriptionRequests = new Map<string, { action: string; response: SubscriptionActionResponse }>()
/** "Recente login" voor gevoelige acties, zoals Laravel `password.confirm` (docs/LARAVEL.md, "Opnieuw wachtwoord bij gevoelige acties"). */
const REAUTH_WINDOW_MS = 15 * 60 * 1000

const undoExpired = () => new ApiError('Terugdraaien kan niet meer.', 409, undefined, 'undo_expired')

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new ApiError('Foto bewaren lukte niet.'))
    reader.readAsDataURL(blob)
  })

/** "lotte.de-vries@…" → "Lotte de vries" — alleen voor nieuwe accounts via een inloglink. */
function nameFromEmail(email: string) {
  const local = email.trim().toLowerCase().split('@')[0] ?? ''
  const name = local.replace(/[._-]+/g, ' ')
  return name.charAt(0).toUpperCase() + name.slice(1)
}

export const demoAccountBackend: AccountBackend = {
  fetchMe: async () => session.read(),

  async login(req) {
    const account = db.accountByEmail(req.email)
    // Zonder wachtwoord (alleen inloglinks) kan inloggen met een wachtwoord niet, net als op de server.
    if (!account || !account.customer.hasPassword || req.password !== DEMO_PASSWORD) {
      await reply(null, 600)
      throw new ApiError('E-mail of wachtwoord klopt niet. Liever een inloglink per mail?', 401)
    }
    session.start(account)
    return reply(account, 600)
  },

  async register(req, claim) {
    if (db.accountByEmail(req.email)) {
      await reply(null, 600)
      throw new ApiError('Dit e-mailadres heeft al een account. Log gewoon in.', 409)
    }
    const account = newDemoAccount({ firstName: req.firstName, email: req.email, hasPassword: true, subscribeIds: claim?.subscribeIds })
    session.start(account)
    // De gastbestelling hoort nu bij dit account — één rij, dus ook meteen in Mijn omgeving.
    const claimed = req.claimOrderId
      ? db.changeOrder(req.claimOrderId, (o) => ({ ...o, customerId: account.customer.id }))
      : null
    // Zat er een kuur in die bestelling, dan komt die nu op het account (met het token van de bestelling).
    if (claimed) linkGuestKuren(account.customer.id, account.customer.email, [claimed.id])
    if (!claimed && claim) {
      db.putOrder({
        id: claim.order.id,
        receivedAt: `${claim.order.date}T12:00`,
        customerId: account.customer.id,
        name: account.customer.firstName,
        city: '',
        fulfilment: 'nieuw',
        request: {
          email: account.customer.email,
          address: { firstName: account.customer.firstName, lastName: account.customer.lastName, ...account.customer.address },
          lines: claim.order.lines.map((l) => ({ productId: l.productId, qty: l.qty, subscribe: claim.order.viaSubscription })),
          code: null,
          skinNote: '',
          refillReminder: false,
        },
        payment: { status: 'betaald', method: 'ideal' },
        gift: null,
        lines: claim.order.lines,
        shipping: claim.order.shipping,
        total: claim.order.total,
        viaSubscription: claim.order.viaSubscription,
      })
    }
    return reply(db.account(account.customer.id) ?? account, 800)
  },

  sendMagicLink: () => reply({ ok: true as const }, 600),

  /** In de demo is het "token" het e-mailadres: doet alsof de klant op de link in de mail klikt. */
  async verifyMagicLink({ token }) {
    // Geen e-mailadres (bijv. `?inloglink=verlopen`) = een verlopen link, zoals de server 422 geeft.
    // Zo is de melding "Deze inloglink is verlopen…" in de demo te testen.
    if (!token.includes('@')) {
      await reply(null, 300)
      throw new ApiError('Deze link is verlopen of al gebruikt.', 422, undefined, 'link_expired')
    }
    // Wie op een link in de mail klikt, heeft dat adres: meteen bevestigd.
    const account =
      db.accountByEmail(token) ?? newDemoAccount({ email: token, firstName: nameFromEmail(token), emailVerified: true, hasPassword: false })
    session.start(account)
    // Bevestigd adres: kuren uit eerdere gastbestellingen op dit adres komen nu op het account.
    linkGuestKuren(account.customer.id, account.customer.email)
    return reply(db.account(account.customer.id) ?? account, 300)
  },

  requestPasswordReset: () => reply({ ok: true as const }, 600),

  /**
   * In de demo is het token het e-mailadres (zoals bij de inloglink). Onbekend → 422 `link_expired`.
   * Het gekozen wachtwoord bewaart de demo niet: inloggen blijft met DEMO_PASSWORD.
   */
  async confirmPasswordReset({ token, password }) {
    const account = db.accountByEmail(token)
    if (!account) {
      await reply(null, 400)
      throw new ApiError('Deze link is verlopen of al gebruikt.', 422, undefined, 'link_expired')
    }
    const weak = passwordProblem(password, { email: account.customer.email, firstName: account.customer.firstName })
    if (weak) {
      await reply(null, 400)
      throw new ApiError(weak, 422, { password: weak })
    }
    // Wie op de link in de mail klikt, heeft dat adres: meteen bevestigd.
    const updated: Account = { ...account, customer: { ...account.customer, hasPassword: true, emailVerified: true } }
    session.start(updated)
    // Adres bevestigd (zoals bij de inloglink): kuren uit gastbestellingen op dit adres komen erbij.
    linkGuestKuren(updated.customer.id, updated.customer.email)
    return reply(db.account(updated.customer.id) ?? updated, 600)
  },

  async logout() {
    db.setSession(null)
  },

  /** In de demo is het token `<klant-id>.<e-mailadres>`: een link voor een oud adres werkt niet meer. */
  async verifyEmail({ token }) {
    // Het klant-id bevat geen punt; het e-mailadres wel, dus alleen op de eerste punt splitsen.
    const dot = token.indexOf('.')
    const id = dot > 0 ? token.slice(0, dot) : ''
    const email = token.slice(dot + 1)
    const account = id ? db.account(id) : null
    if (!account || account.customer.email !== email) {
      await reply(null, 300)
      throw new ApiError('Deze bevestigingslink is verlopen of al gebruikt. Vraag in Mijn omgeving een nieuwe aan.', 422)
    }
    db.changeAccount(id, (a) => [{ ...a, customer: { ...a.customer, emailVerified: true } }, null])
    // Pas nu is het adres van haar: kuren uit eerdere gastbestellingen op dit adres komen erbij.
    linkGuestKuren(id, email)
    return reply({ ok: true as const }, 300)
  },

  async resendVerification() {
    if (!session.read()) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    return reply({ ok: true as const }, 500)
  },

  subscriptionAction: (req) => {
    const { requestId } = req
    // De request is een SubscriptionAction met een extra veld; de regels lezen alleen `type` en zijn velden.
    const action: SubscriptionAction = req
    const fingerprint = JSON.stringify({ ...req, requestId: undefined })
    const seen = subscriptionRequests.get(requestId)
    if (seen) {
      if (seen.action !== fingerprint) throw new ApiError('Deze aanvraag hoort bij een andere wijziging. Probeer het opnieuw.', 422, undefined, 'request_mismatch')
      return reply(seen.response, 200)
    }
    return reply(
      session.change((a) => {
        const subscription = applySubscriptionAction(a.subscription, action, policy.subscription)
        // Alleen een bestaand abonnement dat echt veranderde, is terug te draaien.
        let undoToken: string | null = null
        if (a.subscription && subscription && JSON.stringify(subscription) !== JSON.stringify(a.subscription)) {
          undoToken = newRequestId()
          undoEntries.set(undoToken, {
            customerId: a.customer.id,
            before: snapshotOf(a.subscription),
            after: JSON.stringify(subscription),
            expires: Date.now() + UNDO_TTL_MS,
          })
        }
        const response = { subscription, undoToken }
        subscriptionRequests.set(requestId, { action: fingerprint, response })
        return [{ ...a, subscription }, response]
      }),
      350,
    )
  },

  async undoSubscription({ undoToken }) {
    const entry = undoEntries.get(undoToken)
    undoEntries.delete(undoToken)
    const result = session.change((a) => {
      const current = a.subscription
      if (!entry || !current) throw undoExpired()
      if (entry.customerId !== a.customer.id || entry.expires <= Date.now() || JSON.stringify(current) !== entry.after) {
        throw undoExpired()
      }
      const subscription = restoreSnapshot(current, entry.before)
      return [{ ...a, subscription }, { subscription, undoToken: null }]
    })
    return reply(result, 350)
  },

  requestAppointment: ({ requestId: _requestId, session: _suggested, staffId: preferredStaffId, ...req }) =>
    reply(
      session.change((a) => {
        // Zoals de server: Sarah zette "Niet online laten boeken" aan (409, zonder de reden).
        if (db.bookingBlock(a.customer.id)) throw new ApiError('Online boeken lukt nu niet. Bel of app de salon.', 409, undefined, 'booking_blocked')
        // Zoals de server: buiten de tijden 422 `outside_hours`, intussen bezet 409 `slot_taken`.
        // De uitvoerder volgt uit de salondag (niet uit de keuze van de klant).
        const staffId = assertBookable({ date: req.date, time: req.time, treatmentSlug: req.treatmentSlug, durationMinutes: durationOf(req.treatmentSlug), preferredStaffId })
        const online = localCatalog.treatments.find((t) => t.slug === req.treatmentSlug)?.online
        // Het sessienummer rekent de server zelf (een ander tabblad kan het intussen geboekt hebben).
        const courseSession = sessionFor(a, req.treatmentSlug)
        const appointment: Appointment = {
          id: demoId('af').toLowerCase(),
          kind: online ? 'video' : 'salon',
          status: 'aanvraag',
          request: 'nieuw',
          ...req,
          staffId,
          ...(courseSession ? { session: courseSession } : {}),
        }
        return [{ ...a, appointments: [...a.appointments, appointment] }, appointment]
      }),
      600,
    ),

  rescheduleAppointment: (id, req) =>
    reply(
      session.change((a) => {
        const current = a.appointments.find((x) => x.id === id)
        if (current) {
          assertBookable({ date: req.date, time: req.time, treatmentSlug: current.treatmentSlug, durationMinutes: durationOf(current.treatmentSlug) }, id)
        }
        const appointments = a.appointments.map((x) =>
          x.id === id ? { ...x, ...req, status: 'aanvraag' as const, request: 'verzet' as const } : x,
        )
        const updated = appointments.find((x) => x.id === id)
        if (!updated) throw new ApiError('Deze afspraak bestaat niet meer.', 404)
        return [{ ...a, appointments }, updated]
      }),
      450,
    ),

  cancelAppointment: (id) =>
    reply(
      session.change((a) => [{ ...a, appointments: a.appointments.filter((x) => x.id !== id) }, undefined]),
      450,
    ),

  // Met een backend: multipart naar privé-opslag, EXIF eraf. Hier als data-URL in localStorage.
  async uploadDiaryEntry(req) {
    const current = session.read()
    if (!current) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    const consent = req.storageConsentVersion ?? current.customer.photoStorageConsent
    if (consent !== photoConsent.version) {
      await reply(null, 300)
      throw new ApiError('Geef eerst toestemming om je foto’s te bewaren.', 422, undefined, 'photo_consent_required')
    }
    const image = await blobToDataUrl(req.photo)
    return reply(
      session.change((a) => {
        const entry: DiaryEntry = { id: demoId('foto'), date: today(), image, note: req.note.slice(0, 2000) }
        return [{ ...a, customer: { ...a.customer, photoStorageConsent: consent }, diary: [...a.diary, entry] }, entry]
      }),
      700,
    )
  },

  deleteDiary: () =>
    reply(
      session.change((a) => [{ ...a, customer: { ...a.customer, photoStorageConsent: null, diaryConsent: false }, diary: [] }, undefined]),
      500,
    ),

  async exportData() {
    const a = session.read()
    if (!a) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    // De server levert dit als download, met de foto's als bestanden in een zip.
    return reply({ exportedAt: new Date().toISOString(), ...a }, 600)
  },

  async deleteAccount({ confirmEmail }) {
    const a = session.read()
    if (!a) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    if (confirmEmail.trim().toLowerCase() !== a.customer.email.toLowerCase()) {
      await reply(null, 300)
      throw new ApiError('Dit is niet het e-mailadres van je account.', 422, { confirmEmail: 'Typ het e-mailadres van je account.' })
    }
    // Net als de server (core/types/requests.ts): alleen met een login van < 15 minuten. Wie een
    // open laptop vindt, kan het account zo niet weggooien.
    const at = db.sessionStartedAt()
    if (!at || Date.now() - at > REAUTH_WINDOW_MS) {
      await reply(null, 300)
      throw new ApiError('Log voor de zekerheid nog even opnieuw in.', 423, undefined, 'reauth_required')
    }
    // Tegoed of een lopende kuur is geld van de klant: eerst met de salon afhandelen (terugbetalen
    // of opmaken), anders verdwijnt het met het account. Zelfde code als de server.
    const course = a.course
    if (creditBalanceCents(a.customer.id) > 0 || (course && course.done < course.total)) {
      await reply(null, 300)
      throw new ApiError(
        'Je hebt nog tegoed of een lopende kuur. Neem contact op met de salon, dan regelen we dat eerst samen.',
        409,
        undefined,
        'open_balance',
      )
    }
    db.removeAccount(a.customer.id)
    db.setSession(null)
    return reply(undefined, 700)
  },

  async changePassword({ currentPassword, password }) {
    const a = session.read()
    if (!a) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    const fail = async (err: ApiError): Promise<never> => {
      await reply(null, 500)
      throw err
    }
    if (a.customer.hasPassword) {
      if (currentPassword !== DEMO_PASSWORD) {
        const msg = 'Je huidige wachtwoord klopt niet.'
        await fail(new ApiError(msg, 422, { currentPassword: msg }))
      }
    } else {
      // Instellen zonder huidig wachtwoord: alleen vlak na inloggen (anders kan wie een open laptop vindt het account overnemen).
      const at = db.sessionStartedAt()
      if (!at || Date.now() - at > REAUTH_WINDOW_MS) {
        await fail(new ApiError('Log voor de zekerheid nog even opnieuw in.', 423, undefined, 'reauth_required'))
      }
    }
    const weak = passwordProblem(password, { email: a.customer.email, firstName: a.customer.firstName })
    if (weak) await fail(new ApiError(weak, 422, { password: weak }))
    if (a.customer.hasPassword && password === currentPassword) {
      const msg = 'Kies een ander wachtwoord dan je huidige.'
      await fail(new ApiError(msg, 422, { password: msg }))
    }
    // De server logt hierna de andere apparaten uit; de demo kent maar één sessie.
    db.changeAccount(a.customer.id, (x) => [{ ...x, customer: { ...x.customer, hasPassword: true } }, null])
    return reply(undefined, 600)
  },

  // De demo kent nog geen "verlaten winkelmand"-reeks: aannemen en niets bewaren.
  syncCart: () => reply(undefined, 200),

  async updateCustomer(patch) {
    const current = session.read()
    if (!current) throw new ApiError('Je bent uitgelogd. Log opnieuw in.', 401)
    // Een ander e-mailadres is een andere login: net als wachtwoord en verwijderen alleen vlak na
    // inloggen (wie een open laptop vindt, kan het account zo niet overnemen). Naam, telefoon en
    // adres wijzigen blijft zonder.
    const changesEmail = patch.email !== undefined && patch.email.trim().toLowerCase() !== current.customer.email.toLowerCase()
    const at = db.sessionStartedAt()
    if (changesEmail && (!at || Date.now() - at > REAUTH_WINDOW_MS)) {
      await reply(null, 300)
      throw new ApiError('Log voor de zekerheid nog even opnieuw in om je e-mailadres te wijzigen.', 423, undefined, 'reauth_required')
    }
    return reply(
      session.change((a) => {
        const emailChanged = patch.email !== undefined && patch.email.trim().toLowerCase() !== a.customer.email.toLowerCase()
        // Nieuw adres: opnieuw bevestigen (de server stuurt een link naar het nieuwe adres en een melding naar het oude).
        const customer = { ...a.customer, ...patch, emailVerified: emailChanged ? false : a.customer.emailVerified }
        // Toestemming ingetrokken: markeringen voor marketing vervallen direct.
        const diary = patch.diaryConsent === false ? a.diary.map((d) => ({ ...d, marketing: false })) : a.diary
        return [{ ...a, customer, diary }, customer]
      }),
      400,
    )
  },
}
